import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import { listCourses } from "../../api/assessmentHierarchy";
import {
  SERIES_STATUSES,
  listTestSeries,
  createTestSeries,
  updateTestSeries,
  deleteTestSeries,
  publishTestSeries,
  apiErrorMessage,
} from "../../api/assessmentTestSeries";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentTestSeriesTests from "./AssessmentTestSeriesTests";
import {
  ACCESS_TYPES,
  buildAccessPayload,
  formatSeriesDuration,
  formatSeriesPrice,
  validateSeriesAccess,
} from "./seriesAccess";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const allColumns = [
  { key: "id", label: "ID", cellCss: { minWidth: "60px", width: "60px" }, getValue: (r) => r.id },
  { key: "name", label: "Name", cellCss: { align: "left", textAlign: "left", minWidth: "240px" }, getValue: (r) => r.name },
  { key: "description", label: "Description", cellCss: { minWidth: "260px" }, getValue: (r) => r.description },
  { key: "course", label: "Exam", cellCss: { minWidth: "160px" }, getValue: (r) => r.courseName },
  { key: "access", label: "Access", cellCss: { minWidth: "110px" }, getValue: (r) => r.accessType },
  { key: "price", label: "Price", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.price },
  { key: "duration", label: "Duration", cellCss: { align: "center", minWidth: "110px" }, getValue: (r) => r.durationDays },
  { key: "status", label: "Status", cellCss: { align: "center", minWidth: "120px" }, getValue: (r) => r.status },
];

// The column picker persists which columns the user HID, so columns added later
// show up by default; list a key here to make a column start hidden until toggled.
const DEFAULT_HIDDEN_KEYS = [];
const STORAGE_KEY = "assessmentTestSeriesHiddenColumns";

const statusCfg = {
  DRAFT: { label: "Draft", color: "#b26a00", bg: "#fff3e0" },
  PUBLISHED: { label: "Published", color: "#2e7d32", bg: "#e8f5e9" },
  ARCHIVED: { label: "Archived", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? { label: value ?? "—", color: "#555", bg: "#f5f5f5" };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

StatusChip.propTypes = { value: PropTypes.string };

// Different hues from the status chips so the Access and Status columns don't read alike.
const accessCfg = {
  FREE: { label: "Free", color: "#17479e", bg: "#e8eefb" },
  PREMIUM: { label: "Premium", color: "#6a1b9a", bg: "#f3e5f5" },
};

// A series saved before access settings existed has no accessType; the backend treats that as FREE.
function AccessChip({ value }) {
  const cfg = accessCfg[value] ?? accessCfg.FREE;
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem", color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg }}
    />
  );
}

AccessChip.propTypes = { value: PropTypes.string };

const loadHiddenColumns = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const hidden = JSON.parse(raw);
      if (Array.isArray(hidden)) return new Set(hidden);
    }
  } catch {}
  return new Set(DEFAULT_HIDDEN_KEYS);
};

function SeriesFormDialog({ open, series, courses, onClose, onSave }) {
  const [courseId, setCourseId] = useState("");
  const [name, setName] = useState("");
  const [nameTouched, setNameTouched] = useState(false);
  const [courseTouched, setCourseTouched] = useState(false);
  const [description, setDescription] = useState("");
  const [accessType, setAccessType] = useState("FREE");
  const [price, setPrice] = useState("");
  const [durationDays, setDurationDays] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setNameTouched(false);
    setCourseTouched(false);
    setCourseId(series?.courseId ?? "");
    setName(series?.name || "");
    setDescription(series?.description || "");
    setAccessType(series?.accessType || "FREE");
    setPrice(series?.price ?? "");
    setDurationDays(series?.durationDays ?? "");
  }, [open, series]);

  const accessErrors = validateSeriesAccess({ accessType, price, durationDays });
  const isValid =
    name.trim().length > 0 && courseId !== "" && !accessErrors.price && !accessErrors.durationDays;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: series?.id,
        courseId,
        name: name.trim(),
        description: description.trim(),
        ...buildAccessPayload({ accessType, price, durationDays }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" } }}
    >
      <Box
        sx={{
          position: "relative",
          p: 2.5,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "rgba(255,255,255,0.85)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <CollectionsBookmarkOutlinedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.05rem" }}>
              {series ? "Edit Test Series" : "Add Test Series"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {series ? "Update name and description" : "Pick an exam and name the series"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Stack spacing={2.25} sx={{ pt: 0.5 }}>
          <FormControl
            fullWidth
            size="small"
            required
            disabled={Boolean(series)}
            error={courseTouched && courseId === ""}
          >
            <InputLabel>Exam</InputLabel>
            <Select
              label="Exam *"
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              onBlur={() => setCourseTouched(true)}
              sx={{ borderRadius: 2 }}
            >
              {courses.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
            {series ? (
              <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.5, ml: 1.5 }}>
                A series&apos; course can&apos;t be changed after it is created
              </Typography>
            ) : (
              courseTouched &&
              courseId === "" && (
                <Typography sx={{ fontSize: "0.75rem", color: "error.main", mt: 0.5, ml: 1.75 }}>
                  Please fill out this field
                </Typography>
              )
            )}
          </FormControl>
          <TextField
            label="Series Name"
            fullWidth
            size="small"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setNameTouched(true)}
            error={nameTouched && !name.trim()}
            helperText={nameTouched && !name.trim() ? "Please fill out this field" : undefined}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Access</InputLabel>
            <Select
              label="Access"
              value={accessType}
              onChange={(e) => setAccessType(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {ACCESS_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type === "PREMIUM" ? "Premium (paid)" : "Free"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {accessType === "PREMIUM" && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Price (₹)"
                fullWidth
                size="small"
                required
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                error={Boolean(accessErrors.price)}
                helperText={accessErrors.price}
                inputProps={{ min: 0, step: "0.01" }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <TextField
                label="Access duration (days)"
                fullWidth
                size="small"
                required
                type="number"
                value={durationDays}
                onChange={(e) => setDurationDays(e.target.value)}
                error={Boolean(accessErrors.durationDays)}
                helperText={accessErrors.durationDays}
                inputProps={{ min: 1, step: 1 }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            </Stack>
          )}
          <TextField
            label="Description"
            fullWidth
            size="small"
            multiline
            minRows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": { background: "linear-gradient(135deg, #123a80 0%, #0d47a1 100%)" },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {series ? "Save Changes" : "Create Series"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

SeriesFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  series: PropTypes.object,
  courses: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

function AssessmentTestSeries() {
  const [seriesList, setSeriesList] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  // The backend has no search/sort params, so both act on the current page only.
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [courseFilter, setCourseFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [orderBy, setOrderBy] = useState("id");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [testsTarget, setTestsTarget] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const hidden = loadHiddenColumns();
    const visible = allColumns.map((c) => c.key).filter((k) => !hidden.has(k));
    return new Set(visible.length > 0 ? visible : allColumns.map((c) => c.key));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const coursesById = useMemo(() => new Map(courses.map((c) => [String(c.id), c])), [courses]);

  const loadSeries = () => {
    setLoading(true);
    listTestSeries({
      page,
      size: rowsPerPage,
      courseId: courseFilter || undefined,
      status: statusFilter || undefined,
    })
      .then((data) => {
        setSeriesList(data.content || []);
        setTotalElements(data.totalElements ?? 0);
      })
      .catch((err) => {
        setSeriesList([]);
        setTotalElements(0);
        toast.error(apiErrorMessage(err, "Failed to load test series"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => toast.error(apiErrorMessage(err, "Failed to load exams")));
  }, []);

  useEffect(() => {
    loadSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, courseFilter, statusFilter]);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const rows = useMemo(
    () =>
      seriesList.map((s) => ({
        ...s,
        courseName: coursesById.get(String(s.courseId))?.name || "—",
      })),
    [seriesList, coursesById],
  );

  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return rows;
    const q = debouncedSearch.toLowerCase();
    return rows.filter((s) => s.name?.toLowerCase().includes(q));
  }, [rows, debouncedSearch]);

  const sortKeyOf = { course: "courseName", access: "accessType", duration: "durationDays" };

  const sortedData = useMemo(() => {
    const field = sortKeyOf[orderBy] || orderBy;
    const sortValue = (row) => (field === "price" || field === "durationDays" ? Number(row[field] ?? 0) : row[field]);
    return [...filteredRows].sort((a, b) => {
      const aValue = sortValue(a);
      const bValue = sortValue(b);
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      return order === "asc" ? aValue - bValue : bValue - aValue;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRows, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (series) => {
    setEditing(series);
    setFormOpen(true);
  };

  const handleSave = (values) => {
    const { id, courseId, name, description, ...access } = values;
    const request = id
      ? updateTestSeries(id, { name, description, ...access })
      : createTestSeries({ courseId, name, description, ...access });
    return request
      .then(() => {
        toast.success(id ? "Test series updated successfully!" : "Test series created successfully!");
        setFormOpen(false);
        loadSeries();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to save test series"));
      });
  };

  const handleDelete = () => {
    deleteTestSeries(deleteTarget.id)
      .then(() => {
        toast.success(deleteTarget.status === "DRAFT" ? "Test series deleted!" : "Test series archived!");
        setDeleteTarget(null);
        loadSeries();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to delete test series"));
      });
  };

  const handlePublish = () => {
    setPublishing(true);
    publishTestSeries(publishTarget.id)
      .then(() => {
        toast.success("Test series published!");
        setPublishTarget(null);
        loadSeries();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to publish test series"));
      })
      .finally(() => setPublishing(false));
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: "1 1 600px", alignItems: "center" }}>
              <Tooltip title="Searches the series on the current page" arrow placement="top">
                <TextField
                  size="small"
                  placeholder="Search names"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 220 }}
                />
              </Tooltip>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Exam</InputLabel>
                <Select
                  label="Exam"
                  value={courseFilter}
                  onChange={(e) => {
                    setCourseFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All exams</MenuItem>
                  {courses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  {SERIES_STATUSES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {statusCfg[s].label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{ sx: { width: 220, maxHeight: 500, overflowY: "auto", borderRadius: 2 } }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={<Checkbox checked={visibleColumns.has(col.key)} onChange={() => toggleColumn(col.key)} />}
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{ minWidth: { xs: "120px", md: "auto" }, fontSize: { xs: "10px", md: "12px" } }}
              >
                Add Test Series
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 2, borderRadius: 3 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)", minHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": { color: "#ffffff !important" },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "160px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton columns={visibleColumnDefs.length + 1} />
              ) : sortedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumnDefs.length + 1} sx={{ textAlign: "center", py: 4 }}>
                    <AssessmentEmptyState message="No test series found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                sortedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.02)" } }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell key={c.key} align={c.cellCss?.align} sx={{ padding: "10px 12px", ...c.cellCss }}>
                        {(() => {
                          if (c.key === "id") {
                            return (
                              <Typography sx={{ fontSize: "0.8rem", color: "text.secondary" }}>{row.id}</Typography>
                            );
                          }
                          if (c.key === "name") {
                            return <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>{row.name}</Typography>;
                          }
                          if (c.key === "status") return <StatusChip value={row.status} />;
                          if (c.key === "access") return <AccessChip value={row.accessType} />;
                          if (c.key === "price") return formatSeriesPrice(row);
                          if (c.key === "duration") return formatSeriesDuration(row);
                          if (c.key === "description") {
                            const value = c.getValue(row);
                            return (
                              <Tooltip title={value || ""} arrow disableHoverListener={!value}>
                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "text.secondary",
                                    maxWidth: 260,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {value || "—"}
                                </Typography>
                              </Tooltip>
                            );
                          }
                          return c.getValue(row) ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{ padding: "10px 12px", position: "sticky", right: 0, backgroundColor: "#fff", zIndex: 1 }}
                    >
                      <Stack direction="row" spacing={0.5} justifyContent="flex-start">
                        <Tooltip
                          title={row.status === "DRAFT" ? "Edit Test Series" : "Only draft series can be edited"}
                          arrow
                          placement="top"
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={row.status !== "DRAFT"}
                              onClick={() => openEdit(row)}
                              sx={{ "&:hover": { backgroundColor: "primary.light", color: "white" } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Manage Tests" arrow placement="top">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setTestsTarget(row)}
                            sx={{ "&:hover": { backgroundColor: "info.light", color: "white" } }}
                          >
                            <PlaylistAddCheckOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={row.status === "DRAFT" ? "Delete Test Series" : "Archive Test Series"}
                          arrow
                          placement="top"
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={row.status === "ARCHIVED"}
                              onClick={() => setDeleteTarget(row)}
                              sx={{ "&:hover": { backgroundColor: "error.light", color: "white" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {row.status === "DRAFT" && (
                          <Tooltip title="Publish Test Series" arrow placement="top">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => setPublishTarget(row)}
                              sx={{ "&:hover": { backgroundColor: "success.light", color: "white" } }}
                            >
                              <PublishOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalElements}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": { paddingLeft: 2, paddingRight: 2 },
          }}
        />
      </Paper>

      <SeriesFormDialog
        open={formOpen}
        series={editing}
        courses={courses}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <AssessmentTestSeriesTests
        open={Boolean(testsTarget)}
        series={testsTarget}
        onClose={() => setTestsTarget(null)}
        onChanged={loadSeries}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.status === "DRAFT" ? "Confirm Delete" : "Confirm Archive"}
        message={
          deleteTarget?.status === "DRAFT"
            ? "This draft series and its list of tests will be permanently deleted. The tests themselves are not affected. This action cannot be undone."
            : "This series has been published, so it will be archived instead of deleted. Archived series are hidden from students but keep their history."
        }
        confirmText="DELETE"
        confirmButtonText={deleteTarget?.status === "DRAFT" ? "Delete" : "Archive"}
        cancelButtonText="Cancel"
      />

      <Dialog open={Boolean(publishTarget)} onClose={() => !publishing && setPublishTarget(null)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontWeight: 800, fontSize: "1.05rem", mb: 1 }}>Publish test series?</Typography>
          <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
            Publishing &ldquo;{publishTarget?.name}&rdquo; makes it available to students. After that, its name,
            description, access settings (free or premium, price and duration) and list of tests can no longer be
            changed. Publishing fails if the series has no tests.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPublishTarget(null)} disabled={publishing}>
            Cancel
          </Button>
          <LoadingButton variant="contained" color="success" loading={publishing} onClick={handlePublish}>
            Publish
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AssessmentTestSeries;
